import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import useStore from '../../store';
import './AdminLeaveBalanceVerification.css';

// Groups a raw leave_type string into one of the three base categories
const getLeaveCategory = (leaveType) => {
  const normalized = String(leaveType || '').toLowerCase();
  if (normalized.includes('maternity') || normalized.includes('paternity')) return 'maternity';
  if (normalized.includes('sick')) return 'sick';
  if (normalized.includes('earned') || normalized.includes('vacation')) return 'earned';
  return 'other';
};

const isCancelRow = (leaveType) => /^cancel/i.test(leaveType || '');

// Maps a record to its view tab: 'earned'/'sick'/'maternity' for normal
// entries, 'cancel-earned'/'cancel-sick'/'cancel-maternity' for cancellations
const getViewTabKey = (leaveType) => {
  const category = isCancelRow(leaveType)
    ? getLeaveCategory(leaveType.replace(/^cancel/i, ''))
    : getLeaveCategory(leaveType);
  return isCancelRow(leaveType) ? `cancel-${category}` : category;
};

const AdminLeaveBalanceVerification = () => {
  const { user } = useStore();
  const isAdmin = user?.role === 'admin';
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingUser, setEditingUser] = useState(null);
  const [editValues, setEditValues] = useState({
    earned: 0,
    sick: 0,
    maternity: 0,
  });
  const [viewingUser, setViewingUser] = useState(null);
  const [viewRecords, setViewRecords] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewActiveTab, setViewActiveTab] = useState('earned');

  useEffect(() => {
    fetchLeaveBalances();
  }, []);

  const fetchLeaveBalances = async () => {
    try {
      setLoading(true);
      
      // Fetch all leave balances from the new admin endpoint
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/leaves/all-balances?year=2026`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('aays_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch leave balances: ${response.status}`);
      }

      const data = await response.json();
      
      // Group all records by user
      const userMap = {};
      if (data.data && Array.isArray(data.data)) {
        data.data.forEach((record) => {
          const userId = record.user_id;
          
          if (!userId) return;
          
          if (!userMap[userId]) {
            userMap[userId] = {
              user_id: userId,
              user_name: `${record.first_name} ${record.last_name}`.trim() || 'Unknown',
              email: record.email || '',
              earned: { allocated: 0, taken: 0, rollover: 0 },
              sick: { allocated: 0, taken: 0, rollover: 0 },
              maternity: { allocated: 0, taken: 0, rollover: 0 },
            };
          }

          const leaveType = record.leave_type?.toLowerCase() || '';
          
          if (leaveType.includes('earned')) {
            userMap[userId].earned = {
              allocated: parseFloat(record.total_allocated) || 0,
              taken: parseFloat(record.taken) || 0,
              rollover: parseFloat(record.rollover) || 0,
            };
          } else if (leaveType.includes('sick')) {
            userMap[userId].sick = {
              allocated: parseFloat(record.total_allocated) || 0,
              taken: parseFloat(record.taken) || 0,
              rollover: parseFloat(record.rollover) || 0,
            };
          } else if (leaveType.includes('maternity') || leaveType.includes('paternity')) {
            // Maternity and Paternity are separate leave_balances rows for every
            // employee — sum them into the merged "Maternity/Paternity" bucket
            // instead of letting the second one overwrite the first.
            userMap[userId].maternity = {
              allocated: userMap[userId].maternity.allocated + (parseFloat(record.total_allocated) || 0),
              taken: userMap[userId].maternity.taken + (parseFloat(record.taken) || 0),
              rollover: userMap[userId].maternity.rollover + (parseFloat(record.rollover) || 0),
            };
          }
        });
      }

      const users = Object.values(userMap).sort((a, b) =>
        a.user_name.localeCompare(b.user_name)
      );

      setLeaveBalances(users);
      setFilteredData(users);
      setError(null);
    } catch (err) {
      setError(err.message);
      setLeaveBalances([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    const filtered = leaveBalances.filter((user) =>
      user.user_name.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term)
    );

    setFilteredData(filtered);
  };

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortColumn === column && sortDirection === 'asc') {
      direction = 'desc';
    }

    const sorted = [...filteredData].sort((a, b) => {
      let aVal = '';
      let bVal = '';

      if (column === 'name') {
        aVal = a.user_name;
        bVal = b.user_name;
      } else if (column === 'email') {
        aVal = a.email;
        bVal = b.email;
      }

      if (direction === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });

    setFilteredData(sorted);
    setSortColumn(column);
    setSortDirection(direction);
  };

  const openViewModal = async (user) => {
    setViewingUser(user);
    setViewRecords([]);
    setViewActiveTab('earned');
    setViewLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/admin/master-data?user_id=${user.user_id}&year=2026&limit=500`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('aays_token')}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch leave details: ${response.status}`);
      }
      const data = await response.json();
      const records = (data.data || []).filter(
        (r) => !/^birthday/i.test(r.leave_type)
      );
      setViewRecords(records);
    } catch (err) {
      console.error('Error fetching leave details:', err);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewingUser(null);
    setViewRecords([]);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditValues({
      earned: user.earned.taken || 0,
      sick: user.sick.taken || 0,
      maternity: user.maternity.taken || 0,
    });
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditValues({ earned: 0, sick: 0, maternity: 0 });
  };

  const handleEditChange = (field, value) => {
    setEditValues({
      ...editValues,
      [field]: parseFloat(value) || 0,
    });
  };

  const saveChanges = async () => {
    if (!editingUser) return;

    try {
      const updates = [];
      
      if (editValues.earned !== editingUser.earned.taken) {
        updates.push({
          user_id: editingUser.user_id,
          leave_type: 'earned',
          taken: editValues.earned,
        });
      }
      if (editValues.sick !== editingUser.sick.taken) {
        updates.push({
          user_id: editingUser.user_id,
          leave_type: 'sick',
          taken: editValues.sick,
        });
      }
      if (editValues.maternity !== editingUser.maternity.taken) {
        updates.push({
          user_id: editingUser.user_id,
          leave_type: 'maternity',
          taken: editValues.maternity,
        });
      }

      if (updates.length === 0) {
        closeEditModal();
        return;
      }

      // Send updates to backend
      for (const update of updates) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/leaves/update`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('aays_token')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(update),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to update leave balance`);
        }
      }

      // Update local state
      const updatedBalances = leaveBalances.map((user) => {
        if (user.user_id === editingUser.user_id) {
          return {
            ...user,
            earned: { ...user.earned, taken: editValues.earned },
            sick: { ...user.sick, taken: editValues.sick },
            maternity: { ...user.maternity, taken: editValues.maternity },
          };
        }
        return user;
      });

      setLeaveBalances(updatedBalances);
      setFilteredData(updatedBalances);
      closeEditModal();
      toast.success('Leave balance updated');
    } catch (err) {
      toast.error(`Error saving changes: ${err.message}`);
    }
  };

  const getBalanceStatus = (allocated, taken, rollover) => {
    const available = allocated + rollover - taken;
    if (available < 0) return 'negative-balance';
    if (available < 2) return 'low-balance';
    return 'available';
  };

  const STATUS_BADGE = {
    available: { cls: 'badge-emerald', label: 'Available' },
    'low-balance': { cls: 'badge-amber', label: 'Low Balance' },
    'negative-balance': { cls: 'badge-rose', label: 'Negative Balance' },
  };

  const getOverallStatus = (row) => {
    const statuses = [
      getBalanceStatus(row.earned.allocated, row.earned.taken, row.earned.rollover),
      getBalanceStatus(row.sick.allocated, row.sick.taken, row.sick.rollover),
      getBalanceStatus(row.maternity.allocated, row.maternity.taken, row.maternity.rollover),
    ];
    if (statuses.includes('negative-balance')) return 'negative-balance';
    if (statuses.includes('low-balance')) return 'low-balance';
    return 'available';
  };

  // The number shown alongside the status label — the most constrained
  // (lowest) available balance across Earned, Sick and Maternity/Paternity,
  // matching what actually drives the overall status above.
  const getMinAvailable = (row) => {
    const available = (allocated, taken, rollover) => allocated + rollover - taken;
    return Math.min(
      available(row.earned.allocated, row.earned.taken, row.earned.rollover),
      available(row.sick.allocated, row.sick.taken, row.sick.rollover),
      available(row.maternity.allocated, row.maternity.taken, row.maternity.rollover)
    );
  };

  const exportToCSV = () => {
    const headers = [
      'Employee Name',
      'Email',
      'Earned Leave Taken',
      'Sick Leave Taken',
      'Maternity/Paternity Taken',
    ];

    const rows = filteredData.map((user) => [
      user.user_name,
      user.email,
      user.earned.taken,
      user.sick.taken,
      user.maternity.taken,
    ]);

    const csv = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave-balance-verification-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="leave-verification">
      <div className="page-header">
        <div>
          <div className="page-title">Leave Balance Verification</div>
          <div className="page-subtitle">
            {isAdmin
              ? "Review and verify leave balances for all employees"
              : "Review and verify leave balances for your team"}
          </div>
        </div>
        <button className="btn btn-primary" onClick={exportToCSV} disabled={loading || filteredData.length === 0}>
          Export to CSV
        </button>
      </div>

      {error && (
        <div className="error-alert animate-in">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="card admin-filters animate-in">
        <div className="admin-filters-inner">
          <div className="filter-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Search by name or email…"
              value={searchTerm}
              onChange={handleSearch}
              className="filter-search-input"
            />
          </div>
        </div>
        <span className="filter-count">{filteredData.length} of {leaveBalances.length} employees</span>
      </div>

      <div className="card animate-in stagger-1" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 24 }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8, borderRadius: 8 }} />
            ))}
          </div>
        ) : filteredData.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-title">No employees found</div>
            <div className="empty-state-text">Try adjusting your search.</div>
          </div>
        ) : (
          <div className="table-wrapper" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="sortable">
                    Employee {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="leave-type-header">Earned Leave</th>
                  <th className="leave-type-header">Sick Leave</th>
                  <th className="leave-type-header">Maternity/Paternity</th>
                  <th className="leave-type-header">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row) => {
                  const status = STATUS_BADGE[getOverallStatus(row)];
                  const minAvailable = getMinAvailable(row);
                  return (
                    <tr key={row.user_id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{row.user_name}</div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{row.email}</div>
                      </td>
                      <td className="number-cell">{row.earned.taken}</td>
                      <td className="number-cell">{row.sick.taken}</td>
                      <td className="number-cell">{row.maternity.taken}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${status.cls}`} title="Lowest available balance across Earned, Sick and Maternity/Paternity leave">{status.label} ({minAvailable})</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openViewModal(row)}
                            title="View leave dates taken"
                          >
                            View
                          </button>
                          {isAdmin && (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => openEditModal(row)}
                              title="Edit leave taken values"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingUser && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Leave Taken — {editingUser.user_name}</h3>
              <button className="btn btn-ghost btn-sm" onClick={closeEditModal}>✕</button>
            </div>

            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Earned Leave Taken</label>
                <input
                  type="number"
                  step="0.5"
                  value={editValues.earned}
                  onChange={(e) => handleEditChange('earned', e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Sick Leave Taken</label>
                <input
                  type="number"
                  step="0.5"
                  value={editValues.sick}
                  onChange={(e) => handleEditChange('sick', e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Maternity/Paternity Leave Taken</label>
                <input
                  type="number"
                  step="0.5"
                  value={editValues.maternity}
                  onChange={(e) => handleEditChange('maternity', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeEditModal}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveChanges}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingUser && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Leave Details — {viewingUser.user_name}</h3>
              <button className="btn btn-ghost btn-sm" onClick={closeViewModal}>✕</button>
            </div>

            <div className="view-tabs">
              {[
                { key: 'earned', label: 'Earned/Vacation' },
                { key: 'sick', label: 'Sick Leave' },
                { key: 'maternity', label: 'Maternity/Paternity' },
                { key: 'cancel-earned', label: 'Cancel Earned/Vacation' },
                { key: 'cancel-sick', label: 'Cancel Sick' },
                { key: 'cancel-maternity', label: 'Cancel Maternity/Paternity' },
              ].map((tab) => {
                const count = viewRecords.filter((r) => getViewTabKey(r.leave_type) === tab.key).length;
                return (
                  <button
                    key={tab.key}
                    className={`view-tab ${viewActiveTab === tab.key ? 'active' : ''}`}
                    onClick={() => setViewActiveTab(tab.key)}
                  >
                    {tab.label} <span className="view-tab-count">{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="modal-body">
              {viewLoading ? (
                <div style={{ padding: '24px 0' }}>
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="skeleton" style={{ height: 36, marginBottom: 8, borderRadius: 8 }} />
                  ))}
                </div>
              ) : (() => {
                const tabRecords = viewRecords.filter(
                  (r) => getViewTabKey(r.leave_type) === viewActiveTab
                );
                const totalDays = tabRecords.reduce(
                  (sum, r) => sum + (parseFloat(r.day_count) || 0),
                  0
                );
                return tabRecords.length > 0 ? (
                  <>
                    <p className="view-tab-total">Total: {totalDays} day{totalDays === 1 ? '' : 's'}</p>
                    <div className="table-wrapper" style={{ border: 'none', maxHeight: 360 }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Leave Type</th>
                            <th>Date(s)</th>
                            <th>Days</th>
                            <th>Remark</th>
                            <th>Approved</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tabRecords.map((record) => (
                            <tr key={record.id}>
                              <td>{record.leave_type}</td>
                              <td>
                                {record.start_date === record.end_date
                                  ? record.start_date
                                  : `${record.start_date} to ${record.end_date}`}
                              </td>
                              <td>{record.day_count ?? '—'}</td>
                              <td>{record.remark}</td>
                              <td>{record.approved === true || record.approved === 'true' ? 'Yes' : 'No'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-text">No leave records found in this category.</div>
                  </div>
                );
              })()}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLeaveBalanceVerification;
